import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Trash2, ShoppingBag } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useWishlist } from '@/hooks/useProducts';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

export default function Wishlist() {
  const { t } = useTranslation('account');
  const { data, isLoading, isError } = useWishlist();
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const queryClient = useQueryClient();

  const items = data?.items ?? [];

  async function handleRemove(itemId: string, productName: string) {
    try {
      await api.delete(`/wishlist/${itemId}`);
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      addToast(t('wishlist.toasts.removed', { name: productName }), 'info');
    } catch (err: any) {
      addToast(err.message || t('wishlist.toasts.removeFailed'), 'error');
    }
  }

  async function handleAddToCart(variantId: string, productName: string) {
    try {
      await addItem(variantId);
      addToast(t('wishlist.toasts.added', { name: productName }), 'success');
    } catch (err: any) {
      addToast(err.message || t('wishlist.toasts.addFailed'), 'error');
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">
          {t('wishlist.errorLoading')}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('wishlist.heading')}
        </h2>
        {items.length > 0 && (
          <p className="text-sm text-ink-muted">
            {t('common.itemCount', { count: items.length })}
          </p>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16">
          <Heart
            className="h-16 w-16 text-ink-muted mx-auto mb-4"
            aria-hidden="true"
          />
          <h3 className="text-lg font-semibold text-ink mb-2">
            {t('wishlist.empty')}
          </h3>
          <p className="text-ink-muted mb-6">
            {t('wishlist.emptyDescription')}
          </p>
          <Link to="/shop">
            <Button>{t('common.browseProducts')}</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item: any) => (
            <article
              key={item.id}
              className="bg-surface rounded-xl border border-line overflow-hidden group"
            >
              {/* Image */}
              <Link
                to={`/product/${item.product_slug || item.product_id}`}
                className="block aspect-square overflow-hidden bg-surface-muted relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sage-500"
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.product_name}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag
                      className="h-12 w-12 text-ink-muted"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </Link>

              {/* Content */}
              <div className="p-4">
                <Link
                  to={`/product/${item.product_slug || item.product_id}`}
                  className="block font-medium text-ink hover:text-sage-700 transition-colors truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                >
                  {item.product_name}
                </Link>
                {item.variant_name && (
                  <p className="text-sm text-ink-muted mt-0.5">
                    {item.variant_name}
                  </p>
                )}
                <p className="font-semibold text-sage-700 mt-1">
                  {formatCurrency(Number(item.price))}
                </p>

                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  {item.variant_id && (
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() =>
                        handleAddToCart(item.variant_id, item.product_name)
                      }
                      aria-label={t('wishlist.addToCartAriaLabel', { name: item.product_name })}
                    >
                      <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                      {t('wishlist.addToCart')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(item.id, item.product_name)}
                    aria-label={t('wishlist.removeAriaLabel', { name: item.product_name })}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
